import React, { createContext, useCallback, useContext, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useModal } from '../hooks/useModal';

/**
 * ConfirmProvider / useConfirm (audit 2026-09-20, punto #10).
 *
 * Sostituisce i confirm() nativi: per le azioni distruttive vuoi che l'utente
 * legga davvero, e un dialog di sistema si chiude con Invio per riflesso —
 * oltre a bloccare il thread e non potersi stilizzare, come alert().
 *
 * USO (torna una Promise<boolean>, la funzione chiamante deve essere async):
 *   const confirm = useConfirm();
 *   const ok = await confirm({ message: "Eliminare questo veicolo?" });
 *   if (!ok) return;
 *
 * Per le azioni IRREVERSIBILI più delicate (audit: "esattamente come già fa
 * deleteCompanyNow lato server con confirmRagioneSociale"), richiedi la
 * digitazione di una stringa esatta prima che il pulsante di conferma si
 * attivi:
 *   const ok = await confirm({
 *     message: "Questa azione non è reversibile.",
 *     requireTypedConfirmation: car.targa,
 *     confirmLabel: "Elimina definitivamente",
 *   });
 */
const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [request, setRequest] = useState(null); // { message, title, confirmLabel, cancelLabel, danger, requireTypedConfirmation, resolve }
  const [typedValue, setTypedValue] = useState('');

  const confirm = useCallback((options) => {
    const opts = typeof options === 'string' ? { message: options } : options;
    return new Promise((resolve) => {
      setTypedValue('');
      setRequest({
        title: opts.title || 'Conferma',
        message: opts.message,
        confirmLabel: opts.confirmLabel || 'Conferma',
        cancelLabel: opts.cancelLabel || 'Annulla',
        danger: opts.danger !== false, // di default trattiamo le conferme come azioni delicate (bottone rosso)
        requireTypedConfirmation: opts.requireTypedConfirmation || null,
        resolve,
      });
    });
  }, []);

  const close = (result) => {
    if (request) request.resolve(result);
    setRequest(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {request && <ConfirmDialogView request={request} typedValue={typedValue} setTypedValue={setTypedValue} onClose={close} />}
    </ConfirmContext.Provider>
  );
}

function ConfirmDialogView({ request, typedValue, setTypedValue, onClose }) {
  const { title, message, confirmLabel, cancelLabel, danger, requireTypedConfirmation } = request;
  const canConfirm = !requireTypedConfirmation || typedValue.trim() === requireTypedConfirmation;

  // Audit 2026-09-20, punto #11 applicato anche qui: stesso hook di
  // accessibilità usato dai modali. Escape equivale ad Annulla, MAI a
  // Conferma — coerente con "un dialog di sistema si chiude con Invio per
  // riflesso" che l'audit segnala come rischio da evitare.
  const modalRef = useModal(() => onClose(false));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[100]">
      <div ref={modalRef} role="alertdialog" aria-modal="true" aria-labelledby="confirm-dialog-title" aria-describedby="confirm-dialog-message" className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 modal-scroll">
        <div className="flex items-start gap-3 mb-4">
          {danger && (
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
          )}
          <div>
            <h2 id="confirm-dialog-title" className="text-lg font-bold text-slate-900">{title}</h2>
            <p id="confirm-dialog-message" className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{message}</p>
          </div>
        </div>

        {requireTypedConfirmation && (
          <div className="mb-4">
            <label className="block text-xs font-semibold text-slate-500 mb-1">
              Digita <span className="font-mono font-bold text-slate-800">{requireTypedConfirmation}</span> per confermare
            </label>
            <input
              type="text"
              value={typedValue}
              onChange={(e) => setTypedValue(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
              autoComplete="off"
            />
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={() => onClose(false)}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100"
          >
            {cancelLabel}
          </button>
          <button
            onClick={() => canConfirm && onClose(true)}
            disabled={!canConfirm}
            className={`px-4 py-2 rounded-xl text-sm font-semibold text-white ${
              canConfirm
                ? (danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700')
                : 'bg-slate-300 cursor-not-allowed'
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    console.warn('useConfirm() chiamato fuori da <ConfirmProvider>. Nessun provider trovato nell\'albero.');
    // Fallback degradato ma funzionante, per non rompere flussi in contesti
    // di test dove il provider non è montato.
    // eslint-disable-next-line no-alert
    return (options) => Promise.resolve(window.confirm(typeof options === 'string' ? options : options?.message || ''));
  }
  return ctx;
}
